import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { PlantingFieldEditionPdfDocument } from './PlantingFieldEditionPdfDocument';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#1c1410',
    lineHeight: 1.4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#d4cfc7',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  logo: {
    width: 44,
    height: 44,
    objectFit: 'contain',
  },
  titleBlock: {
    gap: 2,
  },
  docTitle: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: '#2d4a3e',
  },
  docSubtitle: {
    fontSize: 9,
    color: '#6b6560',
  },
  metaBlock: {
    alignItems: 'flex-end',
    gap: 3,
  },
  metaLabel: {
    fontSize: 8,
    color: '#6b6560',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  metaValue: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
  },
  section: {
    marginBottom: 18,
  },
  sectionTitle: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: '#2d4a3e',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e8e4dc',
  },
  groupName: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4,
  },
  muted: {
    color: '#6b6560',
    fontSize: 9,
  },
  table: {
    borderWidth: 1,
    borderColor: '#d4cfc7',
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f2eb',
    borderBottomWidth: 1,
    borderBottomColor: '#d4cfc7',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e8e4dc',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tableRowLast: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  colPlotName: { flex: 2 },
  colAcres: { width: 56, textAlign: 'right' },
  colLocation: { flex: 2 },
  colMaterial: { flex: 1.4 },
  colRate: { width: 64, textAlign: 'right' },
  colLbs: { width: 56, textAlign: 'right' },
  colBags: { width: 44, textAlign: 'right' },
  headerCell: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: '#6b6560',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  cell: {
    fontSize: 9,
  },
  cellBold: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#d4cfc7',
  },
  summaryLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
  },
  summaryValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    color: '#2d4a3e',
  },
  siteGrid: {
    gap: 4,
  },
  siteRow: {
    flexDirection: 'row',
    gap: 8,
  },
  siteKey: {
    width: 88,
    fontSize: 9,
    color: '#6b6560',
  },
  siteVal: {
    flex: 1,
    fontSize: 9,
  },
  footer: {
    position: 'absolute',
    bottom: 32,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: '#e8e4dc',
    paddingTop: 10,
  },
  footerText: {
    fontSize: 8,
    color: '#6b6560',
    marginBottom: 2,
  },
  previewTag: {
    fontSize: 8,
    color: '#8a6914',
    marginTop: 4,
  },
});

function MaterialTableRow({ item, kind, isLast }) {
  if (!item) return null;
  const rowStyle = isLast ? styles.tableRowLast : styles.tableRow;
  return (
    <View style={rowStyle} wrap={false}>
      <Text style={[styles.colMaterial, styles.cell]}>{item.label || kind}</Text>
      <Text style={[styles.colRate, styles.cell]}>{item.rateLbsPerAcre}</Text>
      <Text style={[styles.colLbs, styles.cell]}>{Math.round(item.totalLbs)}</Text>
      <Text style={[styles.colBags, styles.cell]}>{item.bagsNeeded}</Text>
    </View>
  );
}

function SiteRow({ label, value }) {
  if (!value) return null;
  return (
    <View style={styles.siteRow}>
      <Text style={styles.siteKey}>{label}</Text>
      <Text style={styles.siteVal}>{value}</Text>
    </View>
  );
}

export function PlantingEstimatePdfDocument({ data, logoUrl }) {
  if (data?.kind === 'field-edition') {
    return <PlantingFieldEditionPdfDocument data={data} logoUrl={logoUrl} />;
  }
  const {
    generatedAtLabel,
    userDisplayName,
    groupName,
    groupNote,
    clubName,
    isClubGroup,
    zoneLabel,
    soilTypeLabel,
    soilDrainageLabel,
    soilPh,
    soilNotes,
    plots = [],
    totalAcres,
    breakdown,
    ratesAreAdjusted,
  } = data;
  const footerNote = ratesAreAdjusted
    ? 'Material rates adjusted for zone and soil profile where applicable.'
    : 'Material rates shown from preset defaults. Run Calculate recommendations for zone/soil adjustments.';
  const hasSite = Boolean(zoneLabel || soilTypeLabel || soilDrainageLabel || soilPh || soilNotes);

  return (
    <Document title={`Planting Estimate — ${groupName}`} author="Yona">
      <Page size="LETTER" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {logoUrl ? <Image style={styles.logo} src={logoUrl} /> : null}
            <View style={styles.titleBlock}>
              <Text style={styles.docTitle}>Planting Estimate</Text>
              <Text style={styles.docSubtitle}>Yona Planting Assistant</Text>
            </View>
          </View>
          <View style={styles.metaBlock}>
            {userDisplayName ? (
              <>
                <Text style={styles.metaLabel}>Prepared for</Text>
                <Text style={styles.metaValue}>{userDisplayName}</Text>
              </>
            ) : null}
            <Text style={[styles.metaLabel, { marginTop: 6 }]}>Date</Text>
            <Text style={styles.metaValue}>{generatedAtLabel}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Area group</Text>
          <Text style={styles.groupName}>{groupName || 'Untitled group'}</Text>
          {groupNote ? <Text style={styles.muted}>{groupNote}</Text> : null}
          <Text style={[styles.muted, { marginTop: 4 }]}>
            {plots.length} plot{plots.length === 1 ? '' : 's'} · {totalAcres} total acres
          </Text>
        </View>

        {isClubGroup ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Club</Text>
            <Text style={styles.cell}>{clubName}</Text>
            <Text style={[styles.muted, { marginTop: 4 }]}>
              Club-managed planting area. Future: club-level Planting Assistant integration.
            </Text>
          </View>
        ) : null}

        {plots.length > 0 ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Plots</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={[styles.colPlotName, styles.headerCell]}>Plot</Text>
                <Text style={[styles.colAcres, styles.headerCell]}>Acres</Text>
                <Text style={[styles.colLocation, styles.headerCell]}>Location</Text>
              </View>
              {plots.map((plot, index) => (
                <View
                  key={`${plot.name}-${index}`}
                  style={index === plots.length - 1 ? styles.tableRowLast : styles.tableRow}
                  wrap={false}
                >
                  <Text style={[styles.colPlotName, styles.cell]}>{plot.name}</Text>
                  <Text style={[styles.colAcres, styles.cell]}>{plot.acres}</Text>
                  <Text style={[styles.colLocation, styles.cell]}>{plot.location || '—'}</Text>
                </View>
              ))}
            </View>
          </View>
        ) : null}

        {hasSite ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Site conditions</Text>
            <View style={styles.siteGrid}>
              <SiteRow label="USDA zone" value={zoneLabel} />
              <SiteRow label="Soil type" value={soilTypeLabel} />
              <SiteRow label="Drainage" value={soilDrainageLabel} />
              <SiteRow label="Soil pH" value={soilPh} />
              <SiteRow label="Notes" value={soilNotes} />
            </View>
          </View>
        ) : null}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Material estimate</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.colMaterial, styles.headerCell]}>Item</Text>
              <Text style={[styles.colRate, styles.headerCell]}>Lbs/ac</Text>
              <Text style={[styles.colLbs, styles.headerCell]}>Total lbs</Text>
              <Text style={[styles.colBags, styles.headerCell]}>Bags</Text>
            </View>
            <MaterialTableRow item={breakdown?.seed} kind="Seed" isLast={!breakdown?.fertilizer} />
            <MaterialTableRow item={breakdown?.fertilizer} kind="Fertilizer" isLast />
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total material</Text>
            <Text style={styles.summaryValue}>{Math.round(breakdown?.totalLbs || 0)} lbs</Text>
          </View>
          {!ratesAreAdjusted ? (
            <Text style={styles.previewTag}>Preview rates — not yet adjusted for zone/soil</Text>
          ) : null}
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{footerNote}</Text>
          <Text style={styles.footerText}>Generated by Yona Planting Assistant · yona.app</Text>
        </View>
      </Page>
    </Document>
  );
}
